import { useState, useEffect } from "react";
import { Plus, RefreshCw, Trash2, ToggleLeft, ToggleRight, Copy, Check } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAdminAuth } from "../../context/AdminAuthContext";

export default function AccessCodesPage() {
  const { adminAxios } = useAdminAuth();
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ user_id: "", expires_at: "" });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = () => {
    Promise.all([
      adminAxios.get("/admin/access-codes"),
      adminAxios.get("/admin/users"),
    ]).then(([codesRes, usersRes]) => {
      setCodes(codesRes.data);
      setUsers(usersRes.data);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminAxios.post("/admin/access-codes", form);
      setShowForm(false);
      setForm({ user_id: "", expires_at: "" });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Error creating code");
    } finally { setSaving(false); }
  };

  const toggle = async (code) => {
    await adminAxios.put(`/admin/access-codes/${code.id}`, { is_active: !code.is_active });
    load();
  };

  const regen = async (id) => {
    if (!window.confirm("Generate a new code? The old code will stop working.")) return;
    await adminAxios.post(`/admin/access-codes/${id}/regenerate`);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this access code?")) return;
    await adminAxios.delete(`/admin/access-codes/${id}`);
    load();
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const usersWithoutCode = users.filter(u => !codes.find(c => c.user_id === u.id));

  return (
    <div className="p-8 min-h-screen bg-zinc-50">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Access Codes</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{codes.length} codes issued</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-code-btn" className="flex items-center gap-2 bg-[#C8102E] hover:bg-[#A50D25] text-white text-xs tracking-wider uppercase px-5 py-2.5 transition-colors">
          <Plus size={14} strokeWidth={2} /> Generate Code
        </button>
      </div>

      <div className="bg-white border border-zinc-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              {["Code", "User", "Company", "Status", "Expires", "Created", ""].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-zinc-400">Loading...</td></tr>
            ) : codes.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-zinc-400 text-center">No codes generated yet.</td></tr>
            ) : codes.map(c => (
              <tr key={c.id} className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium text-zinc-900 tracking-widest">{c.code}</span>
                    <button onClick={() => copy(c.code)} className="text-zinc-400 hover:text-zinc-700 transition-colors">
                      {copied === c.code ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-700">{c.user ? `${c.user.first_name} ${c.user.last_name}` : "—"}</td>
                <td className="px-4 py-3 text-zinc-500">{c.user?.company || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 font-medium ${c.is_active ? "bg-green-50 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                    {c.is_active ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "No expiry"}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggle(c)} className="text-zinc-400 hover:text-zinc-700 transition-colors" title={c.is_active ? "Disable" : "Enable"}>
                      {c.is_active ? <ToggleRight size={16} className="text-green-500" /> : <ToggleLeft size={16} />}
                    </button>
                    <button onClick={() => regen(c.id)} data-testid={`regen-code-${c.id}`} className="text-zinc-400 hover:text-blue-500 transition-colors" title="Regenerate"><RefreshCw size={13} strokeWidth={1.5} /></button>
                    <button onClick={() => del(c.id)} data-testid={`delete-code-${c.id}`} className="text-zinc-400 hover:text-[#C8102E] transition-colors" title="Delete"><Trash2 size={13} strokeWidth={1.5} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-white max-w-sm p-7">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-zinc-900">Generate Access Code</DialogTitle>
            <DialogDescription className="text-xs text-zinc-400 mt-1">A unique code in SP-XXXX-XX format will be generated automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Assign to User *</label>
              <select value={form.user_id} onChange={e => setForm(f => ({...f, user_id: e.target.value}))} required className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 bg-white" data-testid="code-user-select">
                <option value="">Select user...</option>
                {usersWithoutCode.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} {u.company ? `(${u.company})` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Expiration Date (optional)</label>
              <input type="datetime-local" value={form.expires_at} onChange={e => setForm(f => ({...f, expires_at: e.target.value}))} className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 bg-white" data-testid="code-expiry" />
            </div>
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={saving} data-testid="create-code-btn" className="bg-[#C8102E] hover:bg-[#A50D25] text-white text-xs tracking-wider uppercase px-6 py-2.5 transition-colors disabled:opacity-50">
                {saving ? "Generating..." : "Generate"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="border border-zinc-200 text-zinc-600 text-xs uppercase px-6 py-2.5 hover:bg-zinc-50">
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
